/* ══════════════════════════════════════════════════════════════
   THE TOUR

   Written for someone who has never studied Japanese and has just
   landed on a dashboard full of words they do not recognise. It
   answers, in order: where do I start, what are these sections, and
   what do I have to do daily.

   Deliberately short. A coach-mark tour that runs past about eight
   steps gets skipped, and a skipped tour teaches nothing — so each
   step earns its place or it is cut.

   `target` is a [data-tour] hook rather than a class name, so styling
   changes cannot silently break the tour. A step whose target is
   missing or off-screen falls back to a centred card instead of
   pointing at nothing — see Tour.jsx.
══════════════════════════════════════════════════════════════ */

export const TOUR_STEPS = [
  {
    id: 'welcome',
    route: '/dashboard',
    title: 'Welcome to KitsuSpeak',
    body: 'Japanese looks like a lot at first. It is really just three things learned in order — and this is the tour that shows you where they live. About a minute.',
    cta: 'Show me',
  },
  {
    id: 'path',
    route: '/dashboard',
    target: '[data-tour="learn-current"]',
    title: 'Start here, every time',
    body: 'This winding path is the spine of the whole app, and this is your place on it. Work downward; each step unlocks the next, so you never have to wonder what to study today.',
    placement: 'right',
  },
  {
    id: 'kana',
    target: '[data-tour="nav-kana"]',
    /* Lives in the sidebar, which is a drawer below 1024px — the tour
       opens it before pointing. */
    sidebar: true,
    title: 'First: the two alphabets',
    body: 'Japanese has two, hiragana and katakana, about 46 characters each. Everything else is built on them, so this is where a complete beginner starts. Give it a week.',
    placement: 'right',
  },
  {
    id: 'grammar',
    target: '[data-tour="nav-grammar"]',
    /* Lives in the sidebar, which is a drawer below 1024px — the tour
       opens it before pointing. */
    sidebar: true,
    title: 'Then: how sentences work',
    body: '25 chapters. Each one opens with a plain-English explanation of the pattern, then has you build real sentences from word tiles. No memorising rules you cannot use.',
    placement: 'right',
  },
  {
    id: 'flashcards',
    target: '[data-tour="nav-flashcards"]',
    /* Lives in the sidebar, which is a drawer below 1024px — the tour
       opens it before pointing. */
    sidebar: true,
    title: 'Every day: review',
    body: 'Flashcards bring back words just as you are about to forget them. Ten minutes daily beats two hours on a Sunday — this is the part that actually makes it stick.',
    placement: 'right',
  },
  {
    id: 'tests',
    target: '[data-tour="nav-practice-tests"]',
    /* Lives in the sidebar, which is a drawer below 1024px — the tour
       opens it before pointing. */
    sidebar: true,
    title: 'Later: the real exam',
    body: 'Full JLPT mock papers under real timing, scored by section. Ignore these for now; come back when you are a few months in and want to know where you stand.',
    placement: 'right',
  },
  {
    id: 'streak',
    target: '[data-tour="streak"]',
    title: 'Show up daily',
    body: 'Finish anything at all today and this lights up. Keeping it lit is the single strongest predictor of whether you will still be learning in six months.',
    placement: 'bottom',
  },
  {
    id: 'settings',
    target: '[data-tour="nav-settings"]',
    /* Lives in the sidebar, which is a drawer below 1024px — the tour
       opens it before pointing. */
    sidebar: true,
    title: 'That is the whole tour',
    body: 'You can replay it any time from Account, down here. Now start at the top of the path — hiragana first. It is genuinely the fun part.',
    placement: 'right',
    cta: 'Start learning',
  },
];
